/**
 * StudentsTable Component
 *
 * Таблица учеников на основе DataTable.
 * Центры, группы, предметы и типы отображаются вертикально (один под другим)
 * для удобного считывания соответствия группа ↔ центр ↔ предмет.
 */
import React from 'react';
import { Text, Badge, Stack } from '@mantine/core';
import DataTable from '../common/DataTable';
import tokens from '../../theme/designTokens';

const TYPE_BADGES = {
  b2b: { color: 'blue', label: 'B2B' },
  b2c: { color: 'grape', label: 'B2C' },
  individual: { color: 'orange', label: 'Инд.' },
};

/**
 * Получить конфигурацию колонок таблицы.
 */
const getColumns = (filterOptions = {}) => [
  {
    key: 'full_name',
    label: 'ФИО',
    sortable: true,
    filterable: false,
    searchable: true,
    searchValue: (value, row) =>
      [row.last_name, row.first_name, row.middle_name].filter(Boolean).join(' '),
    renderCell: (value, row) => (
      <Text size="sm" fw={600} c={tokens.colors.neutral[9]}>
        {value || row.first_name || '—'}
      </Text>
    ),
  },
  {
    key: 'date_of_birth',
    label: 'Дата рождения',
    sortable: true,
    filterable: false,
    renderCell: (value) => {
      if (!value) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Text size="sm">
          {new Date(value).toLocaleDateString('ru-RU', {
            day: '2-digit', month: '2-digit', year: 'numeric',
          })}
        </Text>
      );
    },
  },
  {
    key: 'organizations',
    label: 'Центры',
    sortable: false,
    filterable: true,
    filterOptions: (filterOptions.organization_names || []).map((name) => ({
      value: name, label: name,
    })),
    filterDisplayValue: (value, row) =>
      (row.study_groups || []).map((g) => g.organization_name).filter(Boolean).join(', '),
    renderCell: (value, row) => {
      const groups = row.study_groups || [];
      if (groups.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Stack gap={2}>
          {groups.map((g) => (
            <Badge key={`org-${g.id}`} size="xs" variant="filled" color="blue" radius="xl">
              {g.organization_name || '—'}
            </Badge>
          ))}
        </Stack>
      );
    },
  },
  {
    key: 'study_groups',
    label: 'Группы',
    sortable: false,
    filterable: true,
    filterOptions: (filterOptions.group_names || []).map((name) => ({
      value: name, label: name,
    })),
    filterDisplayValue: (value, row) =>
      (row.study_groups || []).map((g) => g.name).join(', '),
    renderCell: (value) => {
      const groups = value || [];
      if (groups.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Stack gap={2}>
          {groups.map((g) => (
            <Badge key={g.id} size="xs" variant="filled" color="indigo" radius="xl">
              {g.name}
            </Badge>
          ))}
        </Stack>
      );
    },
  },
  {
    key: '_subjects',
    label: 'Предмет',
    sortable: false,
    filterable: false,
    renderCell: (value, row) => {
      const groups = row.study_groups || [];
      if (groups.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Stack gap={2}>
          {groups.map((g) => (
            g.subject_name ? (
              <Badge key={`subj-${g.id}`} size="xs" variant="filled" color="teal" radius="xl">
                {g.subject_name}
              </Badge>
            ) : (
              <Text key={`subj-${g.id}`} size="xs" c="dimmed">—</Text>
            )
          ))}
        </Stack>
      );
    },
  },
  {
    key: '_org_type',
    label: 'Тип',
    sortable: false,
    filterable: false,
    renderCell: (value, row) => {
      const groups = row.study_groups || [];
      if (groups.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Stack gap={2}>
          {groups.map((g) => {
            const badge = TYPE_BADGES[g.organization_type] || null;
            return badge ? (
              <Badge key={`type-${g.id}`} size="xs" variant="filled" color={badge.color} radius="xl">
                {badge.label}
              </Badge>
            ) : (
              <Text key={`type-${g.id}`} size="xs" c="dimmed">—</Text>
            );
          })}
        </Stack>
      );
    },
  },
  {
    key: 'parents',
    label: 'Родители',
    sortable: false,
    filterable: true,
    filterOptions: (filterOptions.parent_names || []).map((name) => ({
      value: name, label: name,
    })),
    filterDisplayValue: (value, row) =>
      (row.parents || [])
        .map((p) => [p.last_name, p.first_name].filter(Boolean).join(' '))
        .join(', '),
    renderCell: (value) => {
      const parents = value || [];
      if (parents.length === 0) return <Text size="sm" c="dimmed">—</Text>;
      return (
        <Text size="sm">
          {parents
            .map((p) => [p.last_name, p.first_name].filter(Boolean).join(' ') || p.email)
            .join(', ')}
        </Text>
      );
    },
  },
  {
    key: 'is_active',
    label: 'Статус',
    sortable: false,
    filterable: true,
    filterOptions: [
      { value: 'true', label: 'Активен' },
      { value: 'false', label: 'Неактивен' },
    ],
    filterDisplayValue: (value) => (value ? 'Активен' : 'Неактивен'),
    renderCell: (value) => (
      <Badge size="xs" variant="filled" color={value ? 'green' : 'gray'} radius="xl">
        {value ? 'Активен' : 'Неактивен'}
      </Badge>
    ),
  },
];

/**
 * StudentsTable — таблица учеников с серверной фильтрацией.
 */
const StudentsTable = ({
  data, filters, onFiltersChange, searchValue, onSearchChange,
  onSortChange, onRowClick, filterOptions,
}) => {
  const columns = getColumns(filterOptions || {});

  const handleRenderCell = (row, column) => {
    const colDef = columns.find((c) => c.key === column.key);
    if (colDef?.renderCell) return colDef.renderCell(row[column.key], row);
    return undefined;
  };

  return (
    <DataTable
      columns={columns}
      data={data}
      filters={filters}
      onFiltersChange={onFiltersChange}
      skipProcessing={!!onSearchChange}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      searchValue={searchValue}
      sortable={true}
      onRowClick={onRowClick}
      renderCell={handleRenderCell}
    />
  );
};

export default StudentsTable;
